import { Table, Button, Tag, Tooltip, Image, Modal, Input, Alert } from "antd";
import { useState, useEffect, useRef } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import notfound from "../../assets/images/no-img.png";
import { EyeIcon, Download, Upload } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { buildProductListSearch } from "./productNav";

const { TextArea } = Input;

// Kept in step with the server-side cap in TempProduct.qc_notes.
const QC_NOTES_MAX_LENGTH = 4000;

// `old_price` is only stamped when an admin's edit of a live product actually
// moved the price, so its presence is the price-change signal.
const hasPriceChange = (record) =>
  record?.old_price != null && Number(record.old_price) !== Number(record.price);

function PendingVerification({
  onSwitchTab,
  isActive = true,
  search = "",
  brandId = null,
  categoryId = null,
  segmentId = null,
  vehicleManufacturerId = null,
  vehicleId = null,
  vehicleAddedFilter = null,
  vehicleMappingFilter = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { request } = useRequest();

  // Carry the active tab (and current list state) into the detail page so its
  // Back button returns to this tab instead of the default one.
  const tabSearch = buildProductListSearch(location.search, "pending-qc");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [failModalVisible, setFailModalVisible] = useState(false);
  const [qcNotes, setQcNotes] = useState("");
  const [failLoading, setFailLoading] = useState(false);

  // Product whose edit remark is open in a dialog, null when closed
  const [remarkProduct, setRemarkProduct] = useState(null);

  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const excelInputRef = useRef(null);
  const [excelLoading, setExcelLoading] = useState(false);

  const fetchData = (pag) => {
    setLoading(true);

    let url = `${apiPath.listTempProduct}?status=pending_verification&page=${pag.current}&limit=${pag.pageSize}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (brandId) url += `&brand_id=${brandId}`;
    if (categoryId) url += `&category_id=${categoryId}`;
    if (segmentId) url += `&segment_id=${segmentId}`;
    if (vehicleManufacturerId) url += `&vehicle_manufacturer_id=${vehicleManufacturerId}`;
    if (vehicleId) url += `&vehicle_id=${vehicleId}`;
    if (vehicleAddedFilter) url += `&vehicle_added=${vehicleAddedFilter}`;
    if (vehicleMappingFilter) url += `&vehicle_mapping_option=${vehicleMappingFilter}`;

    request({
      url,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination(prev => ({
          ...prev,
          current: pag.current,
          pageSize: pag.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: () => setLoading(false),
    });
  };

  useEffect(() => {
    // Filters changed -> restart from the first page. The tab pane stays
    // mounted when the user switches away, so becoming active again re-fetches
    // as well; an inactive pane skips the request and reloads on return.
    if (!isActive) return;
    const resetPag = { current: 1, pageSize: pagination.pageSize };
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(resetPag);
  }, [isActive, refresh, search, brandId, categoryId, segmentId, vehicleManufacturerId, vehicleId, vehicleAddedFilter, vehicleMappingFilter]);

  const handleTableChange = (pag) => {
    const newPag = { current: pag.current, pageSize: pag.pageSize };
    setPagination(prev => ({ ...prev, ...newPag }));
    fetchData(newPag);
  };

  const performDownload = (selectedOnly) => {
    const ids = selectedOnly ? selectedRowKeys : [];
    const url = ids.length > 0
      ? `${apiPath.exportTempProduct}?ids=${ids.join(',')}`
      : apiPath.exportTempProduct;

    setDownloadLoading(true);
    request({
      url,
      method: "GET",
      onSuccess: (data) => {
        setDownloadLoading(false);
        setDownloadModalVisible(false);
        if (data.data?.download_url) {
          window.open(data.data.download_url, "_blank");
        }
      },
      onError: (err) => {
        setDownloadLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to export", Severty.ERROR);
      },
    });
  };

  const handleApproveQC = () => {
    if (selectedRowKeys.length === 0) {
      ShowToast("Please select at least one product", Severty.ERROR);
      return;
    }
    Modal.confirm({
      title: "Approve QC",
      content: `Approve ${selectedRowKeys.length} product(s) and promote to live?`,
      onOk: () => {
        request({
          url: apiPath.bulkQcApproveTempProduct,
          method: "POST",
          data: { ids: selectedRowKeys },
          onSuccess: () => {
            ShowToast("Products approved and promoted to live", Severty.SUCCESS);
            setSelectedRowKeys([]);
            setRefresh(prev => !prev);
            onSwitchTab?.('live');
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.message || "Failed to approve", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleFailQC = () => {
    if (selectedRowKeys.length === 0) {
      ShowToast("Please select at least one product", Severty.ERROR);
      return;
    }
    setQcNotes("");
    setFailModalVisible(true);
  };

  const handleFailSave = () => {
    setFailLoading(true);
    request({
      url: apiPath.bulkQcFailTempProduct,
      method: "POST",
      data: { ids: selectedRowKeys, qc_notes: qcNotes || undefined },
      onSuccess: () => {
        setFailLoading(false);
        ShowToast("Products returned to Incomplete", Severty.SUCCESS);
        setFailModalVisible(false);
        setSelectedRowKeys([]);
        setRefresh(prev => !prev);
      },
      onError: (err) => {
        setFailLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to fail QC", Severty.ERROR);
      },
    });
  };

  const handleQcExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    request({
      url: apiPath.bulkQcExcelTempProduct,
      method: "POST",
      data: formData,
      onSuccess: (data) => {
        setExcelLoading(false);
        const { approved = 0, failed = 0, skipped = 0 } = data.data || {};
        ShowToast(`Approved: ${approved}, Failed: ${failed}, Skipped: ${skipped}`, Severty.SUCCESS);
        setRefresh(prev => !prev);
        e.target.value = "";
        if (approved > 0) {
          onSwitchTab?.('live');
        }
      },
      onError: (err) => {
        setExcelLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to process Excel", Severty.ERROR);
        e.target.value = "";
      },
    });
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image",
      render: (image_url) => <Image width={50} height={50} src={image_url || notfound} />,
    },
    {
      title: "Part Number",
      dataIndex: "part_number",
      key: "part_number",
      // Edits of a live product carry the admin's reason; it opens in a dialog
      // rather than a tooltip because a remark can run to several paragraphs.
      render: (val, record) => (
        <div>
          <div>{val || "-"}</div>
          {record.edit_remark && (
            <Tooltip title="Click to read why this was changed">
              <Tag color="processing" onClick={() => setRemarkProduct(record)} className="mt-1 cursor-pointer">
                Remark
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "English Name",
      dataIndex: "en_name",
      key: "en_name",
      render: (val) => val || "-",
    },
    {
      title: "Brand",
      key: "brand",
      render: (_, { brand }) => brand?.en_name || "-",
    },
    {
      title: "Category",
      key: "category",
      render: (_, { category, sub_category }) => (
        <div>
          <div>{category?.en_name || "-"}</div>
          {sub_category?.en_name && (
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub_category.en_name}</div>
          )}
        </div>
      ),
    },
    {
      title: "Segment",
      key: "segment",
      render: (_, { segment }) => segment?.en_name || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      // A price move is the whole point of most live-product edits, so the old
      // value sits next to the proposed one instead of in a column of its own.
      render: (val, record) => {
        if (!hasPriceChange(record)) return val ? `₹${val}` : "-";
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-gray-500 line-through">₹{record.old_price}</span>
            <span className="text-gray-400">→</span>
            <span className="font-semibold text-red-600">₹{val}</span>
          </div>
        );
      },
    },
    {
      title: "Sent to QC At",
      dataIndex: "sent_to_qc_at",
      key: "sent_to_qc_at",
      render: (val) => (val ? moment(val).format("DD-MMM-YYYY HH:mm") : "-"),
    },
    {
      title: "Action",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View" color="purple">
          <Button onClick={() => navigate(`/product/temp/view/${record._id}${tabSearch}`)}>
            <EyeIcon size={15} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <style>{`
        tr.row-mapping-all td:first-child { border-left: 4px solid #52c41a !important; }
        tr.row-mapping-manufacturers td:first-child { border-left: 4px solid #fa8c16 !important; }
        tr.row-mapping-specific td:first-child { border-left: 4px solid #2f54eb !important; }
      `}</style>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setDownloadModalVisible(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-slate-500 text-slate-600 rounded-md bg-transparent hover:bg-slate-500 hover:text-white transition-all duration-200"
        >
          <Download size={14} /> Download {selectedRowKeys.length > 0 ? `Selected (${selectedRowKeys.length})` : "All"}
        </button>

        <button
          disabled={selectedRowKeys.length === 0}
          onClick={handleApproveQC}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-green-500 text-green-600 rounded-md bg-transparent hover:bg-green-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Approve QC {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
        </button>

        <button
          disabled={selectedRowKeys.length === 0}
          onClick={handleFailQC}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-red-500 text-red-600 rounded-md bg-transparent hover:bg-red-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Fail QC {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
        </button>

        <button
          onClick={() => excelInputRef.current?.click()}
          disabled={excelLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-purple-500 text-purple-600 rounded-md bg-transparent hover:bg-purple-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Upload size={14} /> {excelLoading ? "Processing..." : "Upload QC Excel"}
        </button>
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleQcExcelUpload}
        />
      </div>

      <div className="table-responsive customPagination">
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={list}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            defaultPageSize: 20,
            pageSizeOptions: ["10", "20", "30", "50", "100"],
          }}
          onChange={handleTableChange}
          className="ant-border-space"
          onRow={(record) => {
            const mapping = record.vehicle_mapping_option;
            return mapping ? { className: `row-mapping-${mapping}` } : {};
          }}
        />
      </div>

      {/* Download Info Modal */}
      <Modal
        title={<span><Download size={16} className="inline mr-2" />Download QC Review Sheet</span>}
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        footer={null}
        width={600}
      >
        <div className="space-y-4">
          <Alert
            type="info"
            showIcon
            message="How to use the QC Excel sheet"
            description={
              <ol className="mt-2 space-y-1 text-sm list-decimal pl-4">
                <li>Download the sheet - it contains all pending products with their details.</li>
                <li>Fill the <span className="text-red-500">qc_decision</span> column for each row: write <span className="text-green-500">approve</span> to promote to Live, or <span className="text-green-500">fail</span> to return to Incomplete.</li>
                <li>Optionally fill the <span className="text-red-500">qc_notes</span> column with remarks for failed products.</li>
                <li>Upload the filled sheet using the <span className="text-red-500">Upload QC Excel</span> button.</li>
              </ol>
            }
          />

          <div className="bg-gray-50 border rounded-lg p-3 text-sm">
            <div className="font-semibold mb-2 text-gray-700">Column reference:</div>
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 items-start">
                <Tag color="blue" style={{ minWidth: 100, textAlign: 'center' }}>qc_decision</Tag>
                <span className="text-gray-600">
                  <code>approve</code> → promotes to Live Products <code> <br/>
                  fail</code> → returns to Incomplete</span>
              </div>
              <div className="flex gap-2 items-start mt-2">
                <Tag color="orange" style={{ minWidth: 100, textAlign: 'center' }}>qc_notes</Tag>
                <span className="text-gray-600">Optional. Visible to the product team when a product is failed.</span>
              </div>
            </div>
          </div>

          {selectedRowKeys.length > 0 && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
              <span className="text-amber-700"><strong>{selectedRowKeys.length}</strong> product{selectedRowKeys.length > 1 ? "s" : ""} selected.</span>
              <button
                className="text-amber-600 underline text-xs"
                onClick={() => { setSelectedRowKeys([]); }}
              >
                Clear selection to download all
              </button>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setDownloadModalVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={downloadLoading}
              onClick={() => performDownload(selectedRowKeys.length > 0)}
              icon={<Download size={14} />}
            >
              {selectedRowKeys.length > 0
                ? `Download Selected (${selectedRowKeys.length})`
                : `Download All (${pagination.total})`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Remark Modal */}
      <Modal
        title="Why this was changed"
        open={!!remarkProduct}
        onCancel={() => setRemarkProduct(null)}
        width={720}
        footer={<Button onClick={() => setRemarkProduct(null)}>Close</Button>}
      >
        <div className="mt-2 mb-4 text-gray-500">
          Submitted by {remarkProduct?.submitted_by?.name || "an admin"} for{" "}
          {remarkProduct?.part_number || remarkProduct?.en_name || "this product"}.
        </div>

        {hasPriceChange(remarkProduct) && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
            <div className="mb-1 font-medium">Price change</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 line-through">₹{remarkProduct.old_price}</span>
              <span className="text-gray-400">→</span>
              <span className="font-semibold text-red-600">₹{remarkProduct.price}</span>
            </div>
          </div>
        )}

        <div className="mb-4 max-h-[55vh] overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-gray-50 p-4 leading-relaxed">
          {remarkProduct?.edit_remark}
        </div>
      </Modal>

      {/* Fail QC Modal */}
      <Modal
        title="Fail QC"
        open={failModalVisible}
        onCancel={() => setFailModalVisible(false)}
        onOk={handleFailSave}
        confirmLoading={failLoading}
        okText="Confirm Fail"
        okButtonProps={{ danger: true }}
        width={720}
      >
        <p className="mb-2">
          Returning {selectedRowKeys.length} product(s) to Incomplete. Optionally add QC notes:
        </p>
       
        <div className="mb-6">
          <TextArea
            rows={8}
            placeholder="Enter QC notes (optional)"
            value={qcNotes}
            onChange={(e) => setQcNotes(e.target.value)}
            maxLength={QC_NOTES_MAX_LENGTH}
            showCount
          />
        </div>
      </Modal>
    </>
  );
}

export default PendingVerification;
